import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { createForm } from "@seek/forms-ui";
import { useTranslations } from "@vocab/react";
import { Stack, Inline, Text, TooltipRenderer, Box, IconHelp, Autosuggest, filterSuggestions, Columns, Column, TextField, Textarea, Dropdown, Checkbox, Alert, Actions, Button, Heading } from "braid-design-system";
import { forwardRef, useState, useRef, useCallback, useImperativeHandle } from "react";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
import isoCountries from "i18n-iso-countries";
import lang from "i18n-iso-countries/langs/en.json";
import { translations } from "./components/BusinessAddressForm/.vocab/index.mjs";
import { required, minLength, maxLength, badWords, disallowHtml } from "@seek/validators-js";
const defaultOptions = {};
const GetStateByCountryDocument = gql`
    query GetStateByCountry($countryCode: String!) {
  stateByCountry(countryCode: $countryCode) {
    defaultName
  }
}
    `;
function useGetStateByCountryQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(GetStateByCountryDocument, options);
}
const UpdateAccountBillingAddressDocument = gql`
    mutation UpdateAccountBillingAddress($input: AccountBillingAddressInput!) {
  updateAccountBillingAddress(input: $input) {
    message
  }
}
    `;
function useUpdateAccountBillingAddressMutation(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation(UpdateAccountBillingAddressDocument, options);
}
const UpdateAccountPostalAddressDocument = gql`
    mutation UpdateAccountPostalAddress($input: AccountPostalAddressInput!) {
  updateAccountPostalAddress(input: $input) {
    message
  }
}
    `;
function useUpdateAccountPostalAddressMutation(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation(UpdateAccountPostalAddressDocument, options);
}
isoCountries.registerLocale(lang);
const getCountries = () => isoCountries.getNames("en");
const getCountryNameFromCountryCode = (code) => isoCountries.getName(code, "en");
const getCountryCodeFromCountryName = (name) => convertInconsistentCountryName(name) ?? isoCountries.getAlpha2Code(name, "en");
const convertInconsistentCountryName = (name) => {
  switch (name) {
    case "USA":
      return isoCountries.getAlpha2Code("United States of America", "en");
    case "Korea (South)":
      return isoCountries.getAlpha2Code("South Korea", "en");
    case "Samoa (Independent)":
      return isoCountries.getAlpha2Code("Samoa", "en");
    case "Vatican City State (Holy See)":
      return isoCountries.getAlpha2Code("Holy See (Vatican City State)", "en");
    case "Virgin Islands (British)":
      return isoCountries.getAlpha2Code("Virgin Islands, British", "en");
    case "Virgin Islands (American)":
      return isoCountries.getAlpha2Code("Virgin Islands, American", "en");
    case "Bosnia and Herzegowina":
      return isoCountries.getAlpha2Code("Bosnia and Herzegovina", "en");
    case "Burma":
      return isoCountries.getAlpha2Code("Myanmar", "en");
    case "East Timor":
      return isoCountries.getAlpha2Code("Timor-Leste", "en");
    case "England":
      return isoCountries.getAlpha2Code("United Kingdom", "en");
    case "Espana":
      return isoCountries.getAlpha2Code("Spain", "en");
    case "Falkland Islands":
      return isoCountries.getAlpha2Code("Falkland Islands (Malvinas)", "en");
    case "Iran":
      return isoCountries.getAlpha2Code("Iran, Islamic Republic of", "en");
    case "Macau":
      return isoCountries.getAlpha2Code("Macao", "en");
    case "Macedonia":
      return isoCountries.getAlpha2Code("North Macedonia", "en");
    case "St. Helena":
      return isoCountries.getAlpha2Code("Saint Helena", "en");
    case "Swaziland":
      return isoCountries.getAlpha2Code("Eswatini", "en");
    case "Tanzania":
      return isoCountries.getAlpha2Code("Tanzania, United Republic of", "en");
    case "Trinidad":
      return isoCountries.getAlpha2Code("Trinidad and Tobago", "en");
    case "Viet Nam":
      return isoCountries.getAlpha2Code("Vietnam", "en");
    default:
      return void 0;
  }
};
const sortANZFirst = ([keyA, valueA], [keyB, valueB]) => {
  if (keyA === "AU" || keyB === "AU") {
    return keyA === "AU" ? -1 : 1;
  }
  if (keyA === "NZ" || keyB === "NZ") {
    return keyA === "NZ" ? -1 : 1;
  }
  if (valueA < valueB) {
    return -1;
  }
  if (valueA > valueB) {
    return 1;
  }
  return 0;
};
const countryRule = (validatorProps) => ({
  validators: [required, minLength, maxLength],
  validatorProps: {
    min: 4,
    max: 50,
    requiredMessage: validatorProps == null ? void 0 : validatorProps.requiredMessage
  }
});
const addressLinesRule = (validatorProps) => ({
  validators: [required, badWords, minLength, maxLength, disallowHtml],
  validatorProps: {
    min: 4,
    max: 255,
    requiredMessage: validatorProps == null ? void 0 : validatorProps.requiredMessage
  }
});
const suburbRule = (validatorProps) => ({
  validators: [required, maxLength],
  validatorProps: {
    max: 40,
    requiredMessage: validatorProps == null ? void 0 : validatorProps.requiredMessage
  }
});
const stateRule = (validatorProps) => ({
  validators: [required],
  validatorProps: {
    requiredMessage: validatorProps == null ? void 0 : validatorProps.requiredMessage
  }
});
const postCodeRule = (validatorProps) => ({
  validators: (validatorProps == null ? void 0 : validatorProps.isPostCodeRequired) ? [required, maxLength, minLength, badWords, disallowHtml] : [maxLength, minLength, badWords, disallowHtml],
  validatorProps: {
    min: 4,
    max: 10,
    requiredMessage: validatorProps == null ? void 0 : validatorProps.requiredMessage
  }
});
const requiredAutoSuggest = {
  getErrorMessage: async ({ validatorProps }) => validatorProps.requiredLabel,
  test(props) {
    return props.value.value !== void 0;
  }
};
const BUSINESS_ADDRESS_FORM = "BUSINESS_ADDRESS_FORM";
const defaultValues = {
  country: "",
  addressLines: "",
  suburb: "",
  state: "",
  postCode: "",
  countrySelect: {
    text: ""
  }
};
const { FormProvider, useField } = createForm(
  BUSINESS_ADDRESS_FORM,
  { formType: "braid" }
);
const countryOptions = Object.entries(getCountries()).sort(sortANZFirst).map(([countryCode, countryName]) => ({
  text: countryName,
  value: countryCode
}));
const countriesWithoutPostCode = ["FJ", "HK"];
const mapAddressToFormValues = (address) => {
  var _a;
  return {
    country: (address == null ? void 0 : address.countryCode) ? getCountryNameFromCountryCode(address.countryCode) : "",
    addressLines: ((_a = address == null ? void 0 : address.addressLines) == null ? void 0 : _a.join("\n")) ?? "",
    suburb: (address == null ? void 0 : address.suburb) ?? "",
    state: (address == null ? void 0 : address.state) ?? "",
    postCode: (address == null ? void 0 : address.postCode) ?? "",
    countrySelect: (address == null ? void 0 : address.countryCode) ? {
      text: getCountryNameFromCountryCode(address.countryCode),
      value: address.countryCode
    } : defaultValues.countrySelect
  };
};
const evaluateCountry = (formType, values, country) => {
  if (!values.countrySelect.value && !values.country) {
    throw new Error("Country is required");
  }
  if (formType === "billing" && values.countrySelect.value) {
    return values.countrySelect.value;
  }
  return getCountryCodeFromCountryName(values.country) ?? country;
};
const mapFormValuesToAddress = (values, useForBillingAddress, countryCode) => ({
  countryCode,
  addressLines: values.addressLines.split("\n").map((line) => line.trim()).filter(Boolean),
  suburb: values.suburb,
  state: values.state || void 0,
  postCode: values.postCode || void 0,
  useForBillingAddress
});
const Form = forwardRef(
  ({
    onSubmit,
    handleSubmit,
    resetForm,
    onBlur,
    onChange,
    title,
    address,
    description,
    compactHeading = false,
    validateAllFields,
    onCancel,
    displayBillingAddressCheckbox,
    billingAddressCheckboxChecked,
    addressType = "business",
    formError
  }, ref) => {
    const country = address == null ? void 0 : address.countryCode;
    const hideActionButtons = Boolean(ref);
    const { t } = useTranslations(translations);
    const [disablePostCode, setDisablePostCode] = useState(
      countriesWithoutPostCode.includes(country || "")
    );
    const [useForBillingAddress, setUseForBillingAddress] = useState(
      billingAddressCheckboxChecked ?? false
    );
    const lastSelectedCountry = useRef(country);
    const { data, loading, refetch } = useGetStateByCountryQuery({
      variables: {
        countryCode: country || ""
      },
      skip: !country
    });
    const states = (data == null ? void 0 : data.stateByCountry) || [];
    const handleBlur = (event) => {
      if (onBlur) {
        onBlur(event);
      }
    };
    const onSubmitFn = handleSubmit((values) => {
      const countryCode = evaluateCountry(addressType, values, country);
      onSubmit(
        mapFormValuesToAddress(values, useForBillingAddress, countryCode)
      );
    });
    const countryFormField = useField({
      id: "country",
      enabled: !country,
      ...countryRule({
        requiredMessage: t("Country required label")
      })
    });
    const addressLinesField = useField({
      id: "addressLines",
      ...addressLinesRule({ requiredMessage: t("Address required label") })
    });
    const suburbField = useField({
      id: "suburb",
      ...suburbRule({ requiredMessage: t("Suburb required label") })
    });
    const stateField = useField({
      id: "state",
      ...stateRule({ requiredMessage: t("State required label") }),
      active: states.length > 0
    });
    const postCodeField = useField({
      id: "postCode",
      ...postCodeRule({
        requiredMessage: t("Postcode required label"),
        isPostCodeRequired: !disablePostCode
      }),
      active: !disablePostCode
    });
    const countrySelectField = useField({
      id: "countrySelect",
      validators: [requiredAutoSuggest],
      validatorProps: {
        requiredLabel: t("business-address-no-country-select-error-message")
      }
    });
    const handleChange = useCallback(
      (event) => {
        if (event.currentTarget.id === "country") {
          setDisablePostCode(
            countriesWithoutPostCode.includes(
              getCountryCodeFromCountryName(event.currentTarget.value)
            )
          );
          if (countriesWithoutPostCode.includes(
            getCountryCodeFromCountryName(event.currentTarget.value)
          )) {
            postCodeField.onChange("");
          }
        }
        if (onChange) {
          onChange(event);
        }
      },
      [onChange, postCodeField]
    );
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        const handler = handleSubmit((values) => {
          const countryCode = evaluateCountry(addressType, values, country);
          onSubmit(
            mapFormValuesToAddress(values, useForBillingAddress, countryCode)
          );
        });
        handler(new Event("Submit"));
      },
      submitFormAsync: async () => {
        const values = {
          country: countryFormField.value,
          state: stateField.value,
          addressLines: addressLinesField.value,
          suburb: suburbField.value,
          postCode: postCodeField.value,
          countrySelect: countrySelectField.value
        };
        return onSubmit(
          mapFormValuesToAddress(
            values,
            useForBillingAddress,
            evaluateCountry(addressType, values, country)
          )
        );
      },
      resetForm: () => resetForm(),
      validateAllFields
    }));
    const renderHeading = () => {
      if (title) {
        return /* @__PURE__ */ jsx(Fragment, { children: title });
      } else if (displayBillingAddressCheckbox || addressType === "billing") {
        return null;
      }
      return /* @__PURE__ */ jsx(Heading, { level: compactHeading ? "4" : "3", children: t("business-address-section-heading") });
    };
    const renderSubheading = () => {
      if (description) {
        return /* @__PURE__ */ jsx(Fragment, { children: description });
      } else if (displayBillingAddressCheckbox || addressType === "billing") {
        return null;
      }
      return /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("business-address-section-sub-heading") });
    };
    const heading = renderHeading();
    const subHeading = renderSubheading();
    const mapPropsToField = (field) => {
      const fieldOnChange = (event) => {
        field.onChange(event);
        if (handleChange) {
          handleChange(event);
        }
      };
      const fieldOnBlur = (event) => {
        field.onBlur(event);
        if (handleBlur) {
          handleBlur(event);
        }
      };
      return {
        ...field,
        onChange: fieldOnChange,
        onBlur: fieldOnBlur
      };
    };
    const handleCountrySelectOnChange = (suggestion) => {
      const { value } = suggestion;
      countrySelectField.onChange(suggestion);
      setDisablePostCode(countriesWithoutPostCode.includes(value || ""));
      if (countriesWithoutPostCode.includes(value || "")) {
        postCodeField.onChange("");
      }
      if (value && value !== lastSelectedCountry.current) {
        lastSelectedCountry.current = value;
        refetch({ countryCode: value });
        stateField.onChange("");
      }
    };
    const handleClearAutoSuggest = () => {
      countrySelectField.onChange(defaultValues.countrySelect);
    };
    const shouldRenderHeading = Boolean(heading || subHeading);
    return /* @__PURE__ */ jsx("form", { onSubmit: onSubmitFn, children: /* @__PURE__ */ jsxs(Stack, { space: compactHeading ? "large" : "xlarge", children: [
      shouldRenderHeading ? /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        heading,
        subHeading
      ] }) : null,
      /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          Boolean(country) && addressType === "business" && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
              /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("business-address-country-label") }),
              /* @__PURE__ */ jsx(
                TooltipRenderer,
                {
                  id: "countryTooltip",
                  tooltip: /* @__PURE__ */ jsx(Text, { children: t("business-address-country-tooltip") }),
                  children: ({ triggerProps }) => /* @__PURE__ */ jsx(Box, { "aria-label": "Help", ...triggerProps, children: /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", children: /* @__PURE__ */ jsx(IconHelp, {}) }) })
                }
              )
            ] }),
            /* @__PURE__ */ jsx(Text, { children: getCountryNameFromCountryCode(country) }),
            /* @__PURE__ */ jsx(Box, { paddingBottom: "xxsmall" })
          ] }),
          addressType === "billing" && /* @__PURE__ */ jsx(
            Autosuggest,
            {
              ...countrySelectField,
              automaticSelection: true,
              required: true,
              type: "search",
              id: "countrySelect",
              name: "countrySelect",
              onChange: handleCountrySelectOnChange,
              onClear: handleClearAutoSuggest,
              suggestions: filterSuggestions(countryOptions),
              label: t("business-address-country-label"),
              data: { testid: "country-select" },
              reserveMessageSpace: true
            }
          ),
          !country && addressType === "business" && /* @__PURE__ */ jsx(Columns, { space: "small", collapseBelow: "tablet", children: /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(
            TextField,
            {
              name: "country",
              id: "country",
              data: { testid: "country-label" },
              label: t("business-address-country-label"),
              reserveMessageSpace: true,
              ...mapPropsToField(countryFormField)
            }
          ) }) }),
          /* @__PURE__ */ jsx(
            Textarea,
            {
              name: "addressLines",
              id: "addressLines",
              label: t("business-address-lines-label"),
              lines: 2,
              reserveMessageSpace: true,
              ...mapPropsToField(addressLinesField),
              highlightRanges: addressLinesField.highlightRanges || void 0
            }
          ),
          /* @__PURE__ */ jsx(
            TextField,
            {
              id: "suburb",
              label: t("business-address-suburb-label"),
              reserveMessageSpace: true,
              ...mapPropsToField(suburbField)
            }
          ),
          (loading || states.length > 0) && /* @__PURE__ */ jsx(
            Dropdown,
            {
              id: "state",
              label: t("business-address-state-label"),
              reserveMessageSpace: true,
              placeholder: "Select one option",
              ...mapPropsToField(stateField),
              children: states.map(({ defaultName }) => /* @__PURE__ */ jsx("option", { children: defaultName }, defaultName))
            }
          ),
          /* @__PURE__ */ jsx(Columns, { space: "small", collapseBelow: "tablet", children: /* @__PURE__ */ jsx(Column, { width: "1/2", children: !disablePostCode && /* @__PURE__ */ jsx(
            TextField,
            {
              id: "postCode",
              label: t("business-address-postcode-label"),
              reserveMessageSpace: true,
              ...mapPropsToField(postCodeField)
            }
          ) }) }),
          addressType === "business" && displayBillingAddressCheckbox && /* @__PURE__ */ jsx(
            Checkbox,
            {
              onChange: () => {
                setUseForBillingAddress(!useForBillingAddress);
              },
              id: "useForBillingAddress",
              checked: useForBillingAddress,
              label: t("use-for-billing-address-label")
            }
          ),
          formError && /* @__PURE__ */ jsx(Alert, { tone: "critical", children: /* @__PURE__ */ jsx(Text, { children: t("postal-address-form-error") }) })
        ] }),
        !hideActionButtons ? /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "submit",
              tone: "formAccent",
              data: {
                automation: "save"
              },
              children: t("business-address-submit")
            }
          ),
          onCancel && /* @__PURE__ */ jsx(
            Button,
            {
              data: {
                automation: "cancel"
              },
              tone: "formAccent",
              variant: "transparent",
              onClick: onCancel,
              children: t("business-address-cancel")
            }
          )
        ] }) : null
      ] })
    ] }) });
  }
);
const BusinessAddressForm = (props) => /* @__PURE__ */ jsx(
  FormProvider,
  {
    initialValues: {
      ...defaultValues,
      ...mapAddressToFormValues(props.address)
    },
    onSubmitFail: props.onSubmitFail,
    language: props.language === "zh" ? "en" : props.language,
    children: (formProps) => /* @__PURE__ */ jsx(Form, { ...formProps, ...props, ref: props.businessAddressRef })
  }
);
const HirerBusinessAddressForm = (props) => {
  const { onSubmit } = props;
  const [formError, setFormError] = useState(false);
  const [updatePostalAddress] = useUpdateAccountPostalAddressMutation();
  const submitAndUpdate = async (values) => {
    try {
      await updatePostalAddress({
        variables: {
          input: {
            addressLines: values.addressLines.filter(Boolean),
            postCode: values.postCode,
            state: values.state,
            suburb: values.suburb,
            countryCodeAlpha2: values.countryCode,
            useForBillingAddress: values.useForBillingAddress || false
          }
        }
      }).then(() => onSubmit(values));
    } catch (error) {
      console.error(error);
      setFormError(true);
    }
  };
  return /* @__PURE__ */ jsx(
    BusinessAddressForm,
    {
      ...props,
      addressType: "business",
      onSubmit: submitAndUpdate,
      formError
    }
  );
};
const HirerBillingAddressForm = (props) => {
  const { onSubmit } = props;
  const [formError, setFormError] = useState(false);
  const [updateBillingAddress] = useUpdateAccountBillingAddressMutation();
  const submitAndUpdate = async (values) => {
    try {
      await updateBillingAddress({
        variables: {
          input: {
            addressLines: values.addressLines.filter(Boolean),
            postCode: values.postCode,
            state: values.state,
            suburb: values.suburb,
            countryCodeAlpha2: values.countryCode
          }
        }
      }).then(() => onSubmit(values));
    } catch (error) {
      console.error(error);
      setFormError(true);
    }
  };
  return /* @__PURE__ */ jsx(
    BusinessAddressForm,
    {
      ...props,
      addressType: "billing",
      onSubmit: submitAndUpdate,
      formError
    }
  );
};
export {
  BusinessAddressForm,
  HirerBillingAddressForm,
  HirerBusinessAddressForm,
  UpdateAccountBillingAddressDocument,
  UpdateAccountPostalAddressDocument,
  useUpdateAccountBillingAddressMutation,
  useUpdateAccountPostalAddressMutation
};
